import React from "react";
import { Grid } from "@material-ui/core";
import { FoiItem } from "../../domain/FoiItem";
import { SectionProps } from "./SectionProps";
import { Controller } from "react-hook-form";
import { fieldLabels } from "./fieldLabels";
import { Error } from "../common/Error";
import { exemptionsService,
  complexityTypeService,
  foiSourceService,
  foiTypeService,
  focusEnquiryService,
  themeEnquiryService,
  gqvtqService,
  legalInputService,
} from "../../services";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { Select } from "@ofqual/portal-components/components/Select";
import { MarkdownEditor } from "@ofqual/portal-components/components/MarkdownEditor";
import { DatePicker } from "@ofqual/portal-components/components/DatePicker";
import { FileInput } from "@ofqual/portal-components/components/FileInput";
import { AttachmentList } from "../common/AttachmentList";



import {
  qualificationTypeService
} from "../../services";

export const EnquiryDetails: React.FC<{
  sectionProps: SectionProps<FoiItem>;
}> = ({ sectionProps }) => {
  const {
    control,
    errors,
    disabled,
    lookupTracker,
    item,
    deleteAttachment,
    downloadAttachment
  } = sectionProps;

  const [
    exemptionsLookups,
    gqvtqLookups,
    themeEnquiryLookups,
    focusEnquiryLookups,
    foiSourceLookups,
    foiTypeLookups,
    complexityTypeLookups,
    qualificationTypeLookups,
    legalInputLookups
  ] = useLookup(
    exemptionsService,
    gqvtqService,
    themeEnquiryService,
    focusEnquiryService,
    foiSourceService,
    foiTypeService,
    complexityTypeService,
    {
      service: qualificationTypeService,
      id: "id",
      label: "description"
    },
    legalInputService
  );

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <Controller
          rules={{ required: true }}
          name="dateReceived"
          control={control}
          as={
            <DatePicker
              required
              dataTest="dateReceived-picker"
              label="Date Received"
              readOnly={disabled}
            />
          }
        />
        {errors.dateReceived?.type === "required" && (
          <Error data-test="dateReceived-validation">
            This field is required
          </Error>
        )}
      </Grid>
      <Grid item xs={12} sm={6}>
        <Controller
          control={control}
          rules={{ required: true }}
          name="sourceId"
          as={
            <Select
              label={fieldLabels.sourceType}
              data-test="sourceId-field"
              readOnly={disabled}
              required
            >
              {foiSourceLookups}
            </Select>
          }
        />
        {errors.sourceId?.type === "required" && (
          <Error data-test="sourceId-validation">This field is required</Error>
        )}
      </Grid>

      <Grid item xs={12} sm={6}>
        <Controller
          control={control}
          rules={{ required: true }}
          name="caseTypeId"
          as={
            <Select
              label={fieldLabels.caseTypeId}
              data-test="caseTypeId-field"
              readOnly={disabled}
              required
            >
              {foiTypeLookups}
            </Select>
          }
        />
        {errors.caseTypeId?.type === "required" && (
          <Error data-test="caseTypeId-validation">
            This field is required
          </Error>
        )}
      </Grid>

      <Grid item xs={12} sm={6}>
        <Controller
          control={control}
          name="complexityId"
          as={
            <Select
              label={fieldLabels.complexityType}
              data-test="complexityId-field"
              readOnly={disabled}
            >
              {complexityTypeLookups}
            </Select>
          }
        />
      </Grid>

      <Grid item xs={12}>
        <Controller
          control={control}
          name="qualificationTypeId"
          render={(props) => {
            const _onChange = (event: any) => {
              props.onChange(event);
            };
            return (
              <Select
                datatest="qual-type-picklist"
                {...props}
                label={fieldLabels.qualificationTypeId}
                readOnly={disabled}
                lookupTracker={lookupTracker}
                onChange={_onChange}
                selectVariant="multiple"
              >
                {qualificationTypeLookups}
              </Select>
            );
          }}
        />
      </Grid>

      <Grid item xs={12} sm={12}>
        <Controller
          control={control}
          rules={{ required: true, maxLength: 10000 }}
          name="informationRequested"
          render={({ onChange, value }) => (
            <>
              <label>{fieldLabels.informationRequested}</label>
              <MarkdownEditor
                onChange={onChange}
                value={value}
                readOnly={disabled}
                dataTest="info-requested-field"
              />
            </>
          )}
        />
        {errors.informationRequested?.type === "required" && (
          <Error data-test="informationRequested-validation">
            This field is required
          </Error>
        )}
        {errors.informationRequested?.type === "maxLength" && (
          <Error data-test="informationRequested-validation">
            Max length exceeded
          </Error>
        )}
      </Grid>

      <Grid item xs={12} sm={12}>
        <Controller
          control={control}
          rules={{ maxLength: 5000 }}
          name="summary"
          render={({ onChange, value }) => (
            <>
              <label>{fieldLabels.summary}</label>
              <MarkdownEditor
                onChange={onChange}
                value={value}
                readOnly={disabled}
                dataTest="info-summary-field"
              />
            </>
          )}
        />
        {errors.summary?.type === "maxLength" && (
          <Error data-test="summary-validation">Max length exceeded</Error>
        )}
      </Grid>

      <Grid item xs={12} sm={6}>
        <Controller
          control={control}
          rules={{ required: true }}
          name="focusId"
          as={
            <Select
              label={fieldLabels.focusEnquiry}
              data-test="focusId-field"
              readOnly={disabled}
              required
            >
              {focusEnquiryLookups}
            </Select>
          }
        />
        {errors.focusId?.type === "required" && (
          <Error data-test="focusId-validation">This field is required</Error>
        )}
      </Grid>

      <Grid item xs={12} sm={6}>
        <Controller
          control={control}
          rules={{ required: true }}
          name="theme"
          as={
            <Select
              label={fieldLabels.themeEnquiry}
              data-test="theme-field"
              readOnly={disabled}
              required
            >
              {themeEnquiryLookups}
            </Select>
          }
        />
        {errors.theme?.type === "required" && (
          <Error data-test="theme-validation">This field is required</Error>
        )}
      </Grid>

      <Grid item xs={12} sm={6}>
        <Controller
          control={control}
          name="gqvtq"
          as={
            <Select
              label={fieldLabels.gqvtq}
              data-test="gqvtq-field"
              readOnly={disabled}
            >
              {gqvtqLookups}
            </Select>
          }
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <Controller
          control={control}
          name="legalInput"
          as={
            <Select
              label={fieldLabels.legalInput}
              data-test="legal-input-field"
              readOnly={disabled}
            >
              {legalInputLookups}
            </Select>
          }
        />
      </Grid>

      <Grid item xs={12}>
        <Controller
          control={control}
          name="exemptionsApplied"
          render={(props) => {
            const _onChange = (event: any) => {
              props.onChange(event);
            };
            return (
              <Select
                datatest="exemptionsApplied-picklist"
                {...props}
                label={fieldLabels.exemptionsApplied}
                readOnly={disabled}
                lookupTracker={lookupTracker}
                onChange={_onChange}
                selectVariant="multiple"
              >
                {exemptionsLookups}
              </Select>
            );
          }}
        />
      </Grid>
      <Grid item xs={12} sm={12}>
        <Controller
          control={control}
          rules={{ maxLength: 5000 }}
          name="reasonForExtension"
          render={({ onChange, value }) => (
            <>
              <label>{fieldLabels.reasonForExtension}</label>
              <MarkdownEditor
                onChange={onChange}
                value={value}
                readOnly={disabled}
                dataTest="reason-for-extension-field"
              />
            </>
          )}
        />
        {errors.reasonForExtension?.type === "maxLength" && (
          <Error data-test="reason-for-extension-validation">Max length exceeded</Error>
        )}
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="foiFiles"
          defaultValue={[]}
          as={
            <FileInput
              multiple={true}
              dataTest="select-files-button"
              disabled={disabled}
            />
          }
        />
        {item?.supportingDocuments ? (
          <AttachmentList
            canEdit={!disabled}
            attachments={item.supportingDocuments}
            deleteAttachment={deleteAttachment}
            getBlob={downloadAttachment!}
          />
        ) : null}
      </Grid>
    </Grid>
  );
};
